import { useCallback, useMemo, useState } from 'react'
import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useRouter } from 'expo-router'
import { BackButton } from '@/components/ui/BackButton'
import { GlassSurface } from '@/components/ui/GlassSurface'
import { Pressable } from '@/components/ui/Pressable'
import { Scan } from '@/components/ui/icons'
import { MapPreview } from '@/components/explore/MapPreview'
import { MapClusterSheet } from '@/components/explore/MapClusterSheet'
import {
  MAP_CHROME_CONTROL_SIZE,
  MAP_CHROME_RESULTS_HEIGHT,
} from '@/components/explore/map-chrome-hit-targets'
import { buildVehicleMapClusters } from '@/components/explore/map-marker-layout'
import { useVehicles } from '@/lib/hooks/use-vehicles'
import { useLocation } from '@/lib/hooks/use-location'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors } from '@/lib/theme/tokens'
import { FONTS, SPACING, RADIUS, COLORS } from '@/lib/brand'
import { webShadow } from '@/lib/theme/web-shadow'

/** Bigger than the 44pt minimum because a near-miss here pans the map. */
const MAP_BACK_BUTTON_TOUCH_SIZE = 52

export default function MapScreen() {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const router = useRouter()
  const { vehicles } = useVehicles()
  const { location } = useLocation()
  const mapVehicles = useMemo(
    () => buildVehicleMapClusters(vehicles).flatMap((cluster) => cluster.vehicles),
    [vehicles],
  )
  const vehicleCountOnMap = mapVehicles.length
  const [showResults, setShowResults] = useState(false)
  const [fitRequest, setFitRequest] = useState(0)
  const mapChromeTop = Math.max(insets.top + SPACING.md, SPACING.lg)
  const mapChromeBottom = insets.bottom + SPACING.lg

  const handleMapBack = useCallback(() => {
    router.replace('/(tabs)/explore' as never)
  }, [router])
  const handleFitResults = useCallback(() => {
    setFitRequest((request) => request + 1)
  }, [])
  const handleShowResults = useCallback(() => {
    setShowResults(true)
  }, [])
  // This chrome deliberately sits beside, not inside, Mapbox's native texture.
  // A TextureView can paint below a React descendant yet still retain its touch
  // stream. A direct, elevated sibling guarantees the visible control owns the
  // same physical tap on Android.
  const mapChrome = (
    <>
      <View
        pointerEvents="box-none"
        style={{
          position: 'absolute',
          top: mapChromeTop,
          left: SPACING.lg,
          // Android orders touch dispatch for a TextureView at the target,
          // not only at its ancestor. Every control floating over Mapbox must
          // therefore own its elevation, just as the compact "Ver mapa" CTA.
          zIndex: 41,
          elevation: 41,
        }}
      >
        {/* The disc stays the standard 44pt one — a bespoke 52pt disc here
            made the map read as a different app. The TARGET stays 52pt,
            because this control floats over a pannable canvas: missing it
            does not do nothing, it drags the map out from under you. Native
            gets that from hitSlop; web does not, since react-native-web drops
            the prop, so it has to be real geometry. */}
        <BackButton
          onPress={handleMapBack}
          testID="map.back-button"
          touchSize={MAP_BACK_BUTTON_TOUCH_SIZE}
        />
      </View>

      <Pressable
        onPress={handleFitResults}
        accessibilityRole="button"
        accessibilityLabel={t('explore.map_fit_results')}
        testID="map.fit-results"
        style={({ pressed }) => ({
          position: 'absolute',
          top: mapChromeTop,
          right: SPACING.lg,
          width: MAP_CHROME_CONTROL_SIZE,
          height: MAP_CHROME_CONTROL_SIZE,
          zIndex: 41,
          elevation: 41,
          alignItems: 'center',
          justifyContent: 'center',
          opacity: pressed ? 0.82 : 1,
          transform: [{ scale: pressed ? 0.96 : 1 }],
        })}
      >
        <GlassSurface
          tone="regular"
          visualRole="chrome"
          radius={22}
          style={{
            width: 44,
            height: 44,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Scan size={20} color={colors.textPrimary} />
        </GlassSurface>
      </Pressable>

      {/* Every mapped result stays reachable, even when labels overlap. */}
      <Pressable
        onPress={handleShowResults}
        accessibilityRole="button"
        accessibilityLabel={t('explore.map_view_results', { count: vehicleCountOnMap })}
        disabled={vehicleCountOnMap === 0}
        style={{
          position: 'absolute',
          bottom: mapChromeBottom,
          alignSelf: 'center',
          minHeight: MAP_CHROME_RESULTS_HEIGHT,
          zIndex: 41,
          elevation: 41,
          justifyContent: 'center',
          ...webShadow({ y: 2, blur: 8, opacity: 0.2, elevation: 4 }),
        }}
        testID="map.vehicle-count"
      >
        <GlassSurface
          tone="tint"
          visualRole="chrome"
          // The 3D basemap has no predictable quiet patch behind this CTA.
          // A deeper brand tint plus white type keeps it legible over every
          // building and terrain texture while preserving the glass edge.
          tintColor={COLORS.purple.dark}
          radius={RADIUS.xl}
          style={{
            minHeight: MAP_CHROME_RESULTS_HEIGHT,
          }}
          contentStyle={{
            paddingHorizontal: SPACING.md,
            paddingVertical: SPACING.sm,
            justifyContent: 'center',
          }}
        >
          <Text
            style={{
              fontSize: 14,
              color: COLORS.white,
              fontFamily: FONTS.bodyBold,
              textShadowColor: 'rgba(0, 0, 0, 0.22)',
              textShadowOffset: { width: 0, height: 1 },
              textShadowRadius: 2,
            }}
          >
            {t('explore.map_view_results', { count: vehicleCountOnMap })}
          </Text>
        </GlassSurface>
      </Pressable>
    </>
  )

  return (
    <View style={{ flex: 1, backgroundColor: colors.background }} testID="map.screen">
      {/* Full-screen map */}
      <View style={{ flex: 1 }} testID="explore.map-preview">
        <MapPreview
          vehicles={vehicles}
          userLocation={location}
          onViewMap={() => {}}
          onVehiclePress={(id) => router.push(`/explore/${id}` as never)}
          desktop
          hideCountBadge
          fitRequest={fitRequest}
        />
        <View
          pointerEvents="box-none"
          collapsable={false}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            // Keep this in a native sibling above Mapbox's TextureView, not
            // merely visually above it inside the same React subtree.
            zIndex: 80,
            elevation: 80,
          }}
        >
          {mapChrome}
        </View>
      </View>
      {/* AppModal owns a separate native surface. Keep it at screen level;
          only tappable map chrome belongs inside MapPreview's texture stack. */}
      <MapClusterSheet
        vehicles={showResults ? mapVehicles : []}
        title={t('explore.map_results_title')}
        onClose={() => setShowResults(false)}
        onSelect={(id) => {
          setShowResults(false)
          router.push(`/explore/${id}` as never)
        }}
      />
    </View>
  )
}
